import { FC } from 'react'

import { Box, Card, Typography } from '@mui/material'

import { clickableProps } from '../../helpers/interactive'
import { formatDelta } from '../../helpers/scoreDisplay'
import { deltaColor } from '../../helpers/showcase'
import { focusRingSx } from '../../helpers/styles'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import type { StoryPattern } from '../../types'
import { ShowcaseGameIcons } from '../ShowcaseGameIcons/ShowcaseGameIcons'

interface RisingStoryPatternsProps {
  patterns: StoryPattern[]
  onSelect: (patternId: string) => void
  /** Whether to render the top game icons/links on each card. Defaults to true. */
  showGames?: boolean
}

/** Horizontal row of "top rising" story-pattern cards shown on the overview. */
export const RisingStoryPatterns: FC<RisingStoryPatternsProps> = ({ patterns, onSelect, showGames = true }) => {
  const { patternLabel } = useUATaxonomy()

  if (!patterns.length) return null

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
      {patterns.map((pattern) => (
        <Card
          key={pattern.pattern.id}
          variant="outlined"
          {...clickableProps(() => onSelect(pattern.pattern.id))}
          sx={{
            minWidth: 0,
            p: 1.75,
            cursor: 'pointer',
            transition: 'box-shadow 0.15s ease, transform 0.15s ease, border-color 0.15s ease',
            '&:hover': { boxShadow: 3, transform: 'translateY(-1px)', borderColor: 'primary.main' },
            ...focusRingSx(),
          }}
        >
          <Typography sx={{ fontSize: '1.375rem', fontWeight: 'bold', lineHeight: 1.1, color: deltaColor(pattern.deltaPp), mb: 0.25 }}>
            {formatDelta(pattern.deltaPp)}
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, mb: 0.5 }}>{patternLabel(pattern.pattern)}</Typography>
          {pattern.description && (
            <Typography
              sx={{
                fontSize: '0.6875rem',
                color: 'text.secondary',
                lineHeight: 1.4,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {pattern.description}
            </Typography>
          )}
          {showGames && (
            <Box sx={{ mt: 0.75 }}>
              <ShowcaseGameIcons games={pattern.games} max={3} size={24} />
            </Box>
          )}
        </Card>
      ))}
    </Box>
  )
}
